import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Formik, Field } from 'formik';
import ResizeObserver from 'react-resize-observer';
import { useDispatch } from 'react-redux';
import { HiEye, HiEyeOff } from 'react-icons/hi';

import FormLabel from '../../../../../shared/components/FormLabel/FormLabel';
import { passwordRequirements, PasswordSchema } from '../../../../../shared/utils/registrationSchemaHelper';
import Text from '../../../../../shared/_TBComponents/Text';
import { updateCurrentUserProfile } from '../../../../login/ducks/loginActions';

const ColumnWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  padding-top: 15px;
`;

const Container = styled('div')`
  .form-control {
    border: 2px solid #E0E0E0;
    border-radius: 6px;
    padding-left: 7px;
    padding-right: 7px;
    height: 56px;
  }
  .form-control:focus {
    box-shadow: none;
  }
`;

const FieldError = styled('div')`
  margin-top: 10px;
  font-size: 14px;
  color: #ff6600;
`;

const HeaderColumnWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 40px;
`;

const PasswordFieldWrapper = styled('div')`
  position: relative;
`;

const PasswordToggleButton = styled('button')`
  align-items: center;
  background-color: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  justify-content: center;
  padding: 8px;
  pointer-events: auto;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);

  &:focus {
    outline: none;
  }

  &:hover {
    color: #333;
  }
`;

const RowWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SaveButton = styled('button')`
  background-color:  ${(props) => (
    props.theme.colors.green
  )};;
  color: white;
  width:  ${(props) => (props.containerWidth && props.containerWidth < 450 ? '100%' : '49%')};
  height: 50px;
  border-style: none;
  border-radius: 4px;
`;

const ResetPasswordForm = () => {
  const [containerWidth, setContainerWidth] = useState(null);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();

  return (
    <Formik
      initialValues={{
        password: '',
        confirmPassword: '',
      }}
      validationSchema={PasswordSchema}
      onSubmit={(values, { setSubmitting }) => {
        // When button submits form and form is in the process of submitting,
        // submit button is disabled
        setSubmitting(true);
        const { password } = values;
        dispatch(updateCurrentUserProfile({ password }));
        setSubmitting(false);
      }}
    >
      {({
        errors,
        touched,
        handleSubmit,
        isSubmitting,
      }) => (
        <Container>
          <ResizeObserver
            onResize={(rect) => {
              setContainerWidth(rect.width);
            }}
          />
          <form onSubmit={handleSubmit}>
            <HeaderColumnWrapper>
              <Text styledTextProps={{ fontSize: containerWidth && containerWidth < 450 ? 24 : 28, fontWeight: '700' }}>
                Reset Password?
              </Text>
            </HeaderColumnWrapper>
            <ColumnWrapper>
              <RowWrapper>
                <FormLabel size={16}>
                  Password
                </FormLabel>
                <PasswordFieldWrapper>
                  <Field
                    autoComplete='off'
                    className='form-control'
                    type={showPassword ? 'text' : 'password'}
                    name='password'
                    placeholder='Password'
                  />
                  <PasswordToggleButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    tabIndex={-1}
                    type='button'
                  >
                    {showPassword ? 'Hide Password' : 'Show Password'}
                    <span style={{ marginLeft: '8px' }}>
                      {showPassword ? <HiEyeOff /> : <HiEye />}
                    </span>
                  </PasswordToggleButton>
                </PasswordFieldWrapper>
                {errors.password && touched.password
                  ? <FieldError>{errors.password}</FieldError>
                  : (
                    <Text
                      styledTextProps={{ fontSize: '13px', fontWeight: '400' }}
                      styledWrapperProps={{ margin: '4px 0px' }}
                    >
                      {passwordRequirements}
                    </Text>
                  )}
              </RowWrapper>
              <RowWrapper>
                <FormLabel size={16}>
                  Confirm
                </FormLabel>
                <PasswordFieldWrapper>
                  <Field
                    autoComplete='off'
                    className='form-control'
                    type={showConfirmPassword ? 'text' : 'password'}
                    name='confirmPassword'
                    placeholder='Confirm Password'
                  />
                  <PasswordToggleButton
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    tabIndex={-1}
                    type='button'
                  >
                    {showConfirmPassword ? 'Hide Password' : 'Show Password'}
                    <span style={{ marginLeft: '8px' }}>
                      {showConfirmPassword ? <HiEyeOff /> : <HiEye />}
                    </span>
                  </PasswordToggleButton>
                </PasswordFieldWrapper>
                {touched.confirmPassword && errors.confirmPassword && <FieldError className='text-center'>{errors.confirmPassword}</FieldError>}
              </RowWrapper>
            </ColumnWrapper>
            <ColumnWrapper>
              <SaveButton containerWidth={containerWidth} type='submit' disabled={isSubmitting}>
                Click to Reset Password
              </SaveButton>
            </ColumnWrapper>
          </form>
        </Container>
      )}
    </Formik>
  );
};

export default ResetPasswordForm;
